import React from 'react'
import { Loader2, ShieldAlert } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { WorkspaceSummary } from '@/features/onboarding/services/workspaceApi'
import { useCompanyProfile } from '../hooks/useCompanyProfile'
import { IdentityProfileForm, type IdentityProfileFormHandle, type IdentityProfileFormMode } from './IdentityProfileForm'

type Props = {
  workspace: WorkspaceSummary
  onDirtyChange?: (dirty: boolean) => void
}

export type CompanyProfileSectionHandle = IdentityProfileFormHandle

// Only the owner edits the profile (the backend enforces it); admins and members get a
// read-only view. Target customers are edited as ICPs under Sales → ICPs instead.
function formModeForRole(role: WorkspaceSummary['role']): IdentityProfileFormMode {
  return role === 'owner' ? 'edit' : 'read-only'
}

export const CompanyProfileSection = React.forwardRef<CompanyProfileSectionHandle, Props>(
  ({ workspace, onDirtyChange }, ref) => {
    const { currentTheme: t } = useTheme()
    const { profile, loading, saving, error, save, hasCompleteIcp } = useCompanyProfile(workspace.id)

    const mode = formModeForRole(workspace.role)

    if (loading) {
      return (
        <div className="flex justify-center py-16">
          <Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} />
        </div>
      )
    }

    // Nothing to show a member until someone has filled the profile in.
    if (mode === 'read-only' && !profile?.identity) {
      return (
        <div
          className="flex items-start gap-3 rounded-2xl border p-5 text-sm"
          style={{ borderColor: t.border, backgroundColor: t.sectionBackground, color: t.secondrytext }}
        >
          <ShieldAlert size={18} className="mt-0.5 shrink-0" style={{ color: t.warningText }} />
          <p>
            Only the workspace owner can edit the company profile. Your workspace admin has not
            completed it yet — some of Zeph's capabilities are limited until it's done.
          </p>
        </div>
      )
    }

    return (
      <div className="flex flex-col">
        {error && (
          <div className="mb-4 rounded-xl px-4 py-3 text-sm shrink-0" style={{ backgroundColor: t.errorBackground, color: t.errorText }}>
            {error}
          </div>
        )}
        <IdentityProfileForm
          ref={ref}
          workspaceId={workspace.id}
          initial={profile?.identity ?? null}
          saving={saving}
          mode={mode}
          hasCompleteIcp={hasCompleteIcp}
          onSave={save}
          onDirtyChange={onDirtyChange}
        />
      </div>
    )
  },
)

export default React.memo(CompanyProfileSection)
